import React from "react";
import { useModal } from "../../context/modal_provider";
import {
  Box,
  Button,
  Stack,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";

export function useMuteOverviewDialog() {
  const { openDialogModal } = useModal();

  return (mutedAlerts, onUnmute) => {
    openDialogModal({
      dialogTabs: [
        {
          label: "Muted Alerts Overview",
          component: <MuteOverviewDialog mutedAlerts={mutedAlerts} onUnmute={onUnmute} />,
        },
      ],
    });
  };
}

export function MuteOverviewDialog({ mutedAlerts, onUnmute }) {
  const { closeDialogModal } = useModal();
  const [loadingMuteId, setLoadingMuteId] = React.useState(null);
  const [isUnmutingAll, setIsUnmutingAll] = React.useState(false);

  const handleUnmute = async (mutedAlert) => {
    setLoadingMuteId(mutedAlert.id);
    if (onUnmute) {
      await onUnmute(mutedAlert);
    }
    setLoadingMuteId(null);
    closeDialogModal();
  };

  const handleUnmuteAll = async () => {
    setIsUnmutingAll(true);
    for (const mutedAlert of mutedAlerts) {
      if (onUnmute) {
        await onUnmute(mutedAlert);
      }
    }
    setIsUnmutingAll(false);
    closeDialogModal();
  };

  const codeMutes = mutedAlerts.filter((m) => m.alert_code);

  const formatExpiry = (valid_to) => {
    if (!valid_to) return "Never";
    const date = new Date(valid_to * 1000); // Convert to milliseconds
    const now = new Date();
    const diffMs = date - now;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (diffDays > 0) {
      return `${diffDays}d ${diffHours}h`;
    } else if (diffHours > 0) {
      return `${diffHours}h`;
    } else {
      return "Soon";
    }
  };

  return (
    <Box sx={{ minWidth: 600, maxWidth: 800, p: 2 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <NotificationsOffIcon />
            <Typography variant="h6">Muted Alerts</Typography>
          </Stack>
          {mutedAlerts.length > 0 && (
            <Button
              size="small"
              color="error"
              onClick={handleUnmuteAll}
              disabled={isUnmutingAll || loadingMuteId !== null}
              startIcon={isUnmutingAll ? <CircularProgress size={16} /> : undefined}
            >
              {isUnmutingAll ? "Unmuting..." : "Unmute All"}
            </Button>
          )}
        </Stack>

        {mutedAlerts.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No muted alerts
            </Typography>
          </Box>
        ) : (
          <>
            {/* Alert Type Mutes */}
            {codeMutes.length > 0 && (
              <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Muted Alert Types
                  <Chip label={codeMutes.length} size="small" />
                </Typography>
                <List>
                  {codeMutes.map((mutedAlert, idx) => (
                    <ListItem
                      key={idx}
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: "background.paper",
                      }}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => handleUnmute(mutedAlert)}
                          size="small"
                          color="error"
                          disabled={loadingMuteId === mutedAlert.id || isUnmutingAll}
                        >
                          {loadingMuteId === mutedAlert.id ? (
                            <CircularProgress size={20} color="error" />
                          ) : (
                            <DeleteIcon />
                          )}
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2">{mutedAlert.info || "Unknown Alert Type"}</Typography>
                            <Chip
                              label={`Device: ${mutedAlert.device_id}`}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20 }}
                            />
                          </Stack>
                        }
                        secondary={
                          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Muted: {new Date(mutedAlert.valid_from * 1000).toLocaleString()} by{" "}
                              <i>{mutedAlert.muted_by}</i>
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="caption" color="text.secondary">
                                Expires in:
                              </Typography>
                              <Chip
                                label={formatExpiry(mutedAlert.valid_to)}
                                size="small"
                                color={mutedAlert.valid_to ? "default" : "warning"}
                                sx={{ height: 20 }}
                              />
                            </Stack>
                          </Stack>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </>
        )}

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={closeDialogModal} variant="contained">
            Close
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
